import RemoteCollection from "#root/utility/remote-collection";
import * as z from "zod";

export const NewsRPMInstanceListItemSchema = z.object({
  code: z.string(),
  networkCode: z.string().nullish(),
  uri: z.string(),
});

export function getNewsRPMInstanceByCode(instanceId: string) {
  return new RemoteCollection(`/api/newsrpm/instance/${instanceId}`, NewsRPMInstanceListItemSchema);
}

export const NewsRPMInstanceFilterListItemSchema = z.object({
  id: z.number(),
  field: z.string().default("Unknown Field"),
  value: z.string().default("Unknown Value"),
});

export const NewsRPMInstanceFilterDetailItemSchema = NewsRPMInstanceFilterListItemSchema.extend({
  editorialAction: z.string().nullish(),
  qualityAdjustment: z.number().default(0),
  library: z.string().nullish(),
}).loose();

export type NewsRPMInstanceFilterListItem = z.output<typeof NewsRPMInstanceFilterListItemSchema>;
export type NewsRPMInstanceFilterDetailItem = z.output<typeof NewsRPMInstanceFilterDetailItemSchema>;

export function getNewsRPMInstanceFiltersCollection(instanceId: string) {
  return new RemoteCollection(`/api/newsrpm/instance/${instanceId}/admin/filter`, NewsRPMInstanceFilterListItemSchema, NewsRPMInstanceFilterDetailItemSchema);
}

export const NewsRPMInstanceDestinationListItemSchema = z.object({
  id: z.number(),
  name: z.string().default("Unknown Name"),
  type: z.string().default("Unknown Type"),
});

export const NewsRPMInstanceDestinationDetailItemSchema = NewsRPMInstanceDestinationListItemSchema.extend({
  filter: z.unknown().nullish(),
  options: z.unknown().nullish(),
}).loose();
export type NewsRPMInstanceDestinationListItem = z.infer<typeof NewsRPMInstanceDestinationListItemSchema>;
export type NewsRPMInstanceDestinationDetailItem = z.infer<typeof NewsRPMInstanceDestinationDetailItemSchema>;

export function getNewsRPMInstanceDestinationCollection(instanceId: string) {
  return new RemoteCollection(
    `/api/newsrpm/instance/${instanceId}/admin/destination`,
    NewsRPMInstanceDestinationListItemSchema,
    NewsRPMInstanceDestinationDetailItemSchema,
  );
}

export const NewsRPMInstanceArticleListItemSchema = z.object({
  id: z.number(),
  date: z.string(),
  headline: z.string(),
  provider: z.string().nullish(),
  slug: z.string(),
});
export const NewsRPMInstanceArticleDetailItemSchema = NewsRPMInstanceArticleListItemSchema.extend({
  summary: z.string().nullish(),
  firstParagraph: z.string().nullish(),
  publisher: z.string().nullish(),
  expires: z.number().nullish(),
  link: z.string().nullish(),
  visiblity: z.string().nullish(),
  metaData: z.unknown().nullish(),
  normalizedData: z.unknown().nullish(),
  sponsored: z.boolean().nullish(),
  quality: z.number().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
  type: z.string().nullish(),
});

export function createNewsRPMInstanceArticleSlugCollection(instanceId: string) {
  const instanceCollection = new RemoteCollection(
    `/api/newsrpm/instance/${instanceId}`,
    NewsRPMInstanceArticleListItemSchema,
    NewsRPMInstanceArticleDetailItemSchema,
  );

  const newCollection = Object.create(instanceCollection) as typeof instanceCollection;

  newCollection.details = async slug => {
    const response = await instanceCollection.fetch(`article/slug/${slug}`, {
      query: { includeInvalidArticles: 1 },
    });
    const data = await response.json();

    return { success: true, row: NewsRPMInstanceArticleDetailItemSchema.parse(data.doc) };
  };
  newCollection.addOrUpdate = async (id, record) => {
    const response = await instanceCollection.fetch("article", {
      method: "POST",
      body: record,
    });

    await response.json();

    return { success: true, updated: String(id), row: record };
  };

  newCollection.list = async ({ limit, offset } = {}) => {
    const response = await instanceCollection.fetch("search/article", {
      method: "POST",
      body: {
        includeInvalidArticles: 1, // TODO: NewsRPM API doesn't support this yet
        count: limit,
        offset,
      },
    });

    return response.json();
  };

  newCollection.search = async ({ search, limit, offset }) => {
    const response = await instanceCollection.fetch("search/article", {
      method: "POST",
      body: {
        includeInvalidArticles: 1, // TODO: NewsRPM API doesn't support this yet'
        fullText: search,
        count: limit,
        offset,
      },
    });

    return response.json();
  };
  return newCollection;
}

export const NewsRPMInstanceFeedListItemSchema = z.object({
  id: z.number(),
  name: z.string().default("Unknown Feed"),
  type: z.string().default("Unknown Feed Type")
});
export const NewsRPMInstanceFeedDetailItemSchema = NewsRPMInstanceFeedListItemSchema.extend({
  url: z.string().nullish(),
  enabled: z.number().nullish(),
  lastUpdated: z.string().nullish(),
});
export type NewsRPMInstanceFeedListItem = z.output<typeof NewsRPMInstanceFeedListItemSchema>;
export type NewsRPMInstanceFeedDetailItem = z.output<typeof NewsRPMInstanceFeedDetailItemSchema>;

export function getNewsRPMInstanceFeedCollection(instanceId: string) {
  return new RemoteCollection(`/api/newsrpm/instance/${instanceId}/admin/feed`, NewsRPMInstanceFeedListItemSchema, NewsRPMInstanceFeedDetailItemSchema);
}